import { Button, Flex, Form, Space, Spin } from "antd"
import PropTypes from "prop-types"
import { colors, spacings } from "themes/variables"
import { Text } from "ui/Typography"
import PaymentMethodForm from "pagesV2/pro/Bookings/BookingForm/PaymentMethod"
import StripeWrapper from "components/stripe/StripeWrapper"
import { useTranslation } from "react-i18next"
import { useEffect, useState } from "react"
import { adjustBookingPrice, captureGuideBookingIncomeRecord, getClientBookingDetails } from "store/bookings"
import { useDispatch, useSelector } from "react-redux"
import { toaster } from "components/common/Toast"
import { useStripe } from "@stripe/react-stripe-js"
import { selectedPaymentMethodIdSelector, selectedCardLast4Selector } from "store/user"
import PriceAndReasonFields from "../PriceAndReasonFields"
import { numberToCurrency } from "helpers/string"

const CardPaymentForm = ({ currentPrice, bookingUUID, bookingId, onClose }) => {
  const [form] = Form.useForm()
  const { t } = useTranslation()
  const [hasErrors, setHasErrors] = useState(true)
  const dispatch = useDispatch()
  const stripe = useStripe()
  const [paymentMethodError, setPaymentMethodError] = useState("")
  const selectedPaymentMethodId = useSelector(selectedPaymentMethodIdSelector)
  const selectedCardLast4 = useSelector(selectedCardLast4Selector)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleFieldsChange = () => {
    const amountError = form.getFieldError("amount")
    const touched = form.isFieldTouched("amount")
    const reasonError = form.getFieldError("reason")
    const touchedReason = form.isFieldTouched("reason")
    setHasErrors(amountError.length > 0 || !touched || !!paymentMethodError || reasonError.length > 0 || !touchedReason)
  }

  useEffect(() => {
    form.setFieldValue("paymentType", "card")
  }, [form])

  const handleSubmit = (data) => {
    const paymentType = form.getFieldValue("paymentType")
    const isPaymentLink = paymentType === "link"

    if (paymentType === "card" && !selectedPaymentMethodId) {
      setPaymentMethodError(t("client.payment.paymentMethodError"))
      setHasErrors(true)
      return
    } else {
      setPaymentMethodError("")
    }
    setIsSubmitting(true)
    dispatch(adjustBookingPrice(bookingId, { new_price: Number(data?.amount), reason: data?.reason, stripe: true, isLink: isPaymentLink }))
      .then(async (response) => {
        try {
          if (!isPaymentLink && response?.client_secret) {
            const result = await stripe.confirmCardPayment(response.client_secret, {
              payment_method: selectedPaymentMethodId
            })

            if (result?.error) {
              toaster.error(result.error.message)
              console.error("Stripe Error:", result.error)
              setIsSubmitting(false)
              return
            }

            const paymentIntentId = result?.paymentIntent?.id
            if (!paymentIntentId) {
              setIsSubmitting(false)
              return
            }

            const captureRes = await dispatch(captureGuideBookingIncomeRecord(bookingId, response?.income?.id))
            if (captureRes?.status === "succeeded") {
              toaster.success(`Card ending in ****${selectedCardLast4} has been charged`)
              form.resetFields()
            }
          }
          dispatch(getClientBookingDetails(bookingUUID))
          form.resetFields()
          onClose()
        } catch (error) {
          console.error("Error:", error)
        }
      })
      .catch((error) => {
        if (error?.data?.is_amount_lower) {
          setHasErrors(true)
          form.setFields([
            {
              name: "amount",
              errors: [error?.data?.message]
            }
          ])
        }
      })
      .finally(() => {
        setIsSubmitting(false)
      })
  }

  return (
    <Spin spinning={isSubmitting} tip="Processing payment...">
      <StripeWrapper>
        <Space vertical size={24} className="w-full mt-3xl" direction="vertical">
          <Flex gap={spacings.S} style={{ backgroundColor: colors.surface1 }} className="py-xs px-s">
            <Text color={colors.textSecondaryDark}>Current Price: </Text>
            <Text color={colors.textPrimary}>{numberToCurrency(currentPrice)}</Text>
          </Flex>
          <Form layout="vertical" form={form} onFinish={handleSubmit} onFieldsChange={handleFieldsChange}>
            <div className="w-full mb-l">
              <PriceAndReasonFields />
            </div>
            <PaymentMethodForm form={form} bookingId={bookingUUID} isCashPaymentRequired={false} />
            {paymentMethodError && <div style={{ color: "red", marginTop: 4 }}>{paymentMethodError}</div>}
            <Flex gap={spacings.S} className="mt-xl">
              <Button className="py-l px-xl" color="primary" variant="outlined" onClick={onClose}>
                {t("global.cancel")}
              </Button>
              <Button type="primary" className="py-l px-xl" htmlType="submit" disabled={hasErrors}>
                {t("global.submit")}
              </Button>
            </Flex>
          </Form>
        </Space>
      </StripeWrapper>
    </Spin>
  )
}

CardPaymentForm.propTypes = {
  bookingUUID: PropTypes.string,
  bookingId: PropTypes.number,
  onClose: PropTypes.func,
  currentPrice: PropTypes.number,
}

export default CardPaymentForm
